import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { formatMoney, formatMoneyFromUnknown, resolveCurrencyCode } from '@/lib/money'
import { useCurrentStore } from '../../context/CurrentStoreContext'
import { useTransaction } from '../../context/TransactionContext'
import { Card, CardHeader } from '../../components/ui/Card'
import { SkeletonDetailFields } from '../../components/ui/Skeleton'

export default function TransactionDetail() {
  const { id } = useParams()
  const { selectedTransaction, getTransactionById, error, clearError } = useTransaction()
  const { storeRecord } = useCurrentStore()

  useEffect(() => {
    if (!id) return
    clearError()
    getTransactionById(decodeURIComponent(id)).catch(() => {})
  }, [id, getTransactionById, clearError])

  const isPos = selectedTransaction?.kind === 'pos_sale'
  const storeCurrency = resolveCurrencyCode(storeRecord?.currency)
  const currency = resolveCurrencyCode(selectedTransaction?.currency || storeCurrency)

  return (
    <Card>
      <CardHeader
        title="Transaction details"
        description="Wallet, payout, online store, or in-store POS — context depends on channel."
      />
      {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
      {!selectedTransaction ? (
        <SkeletonDetailFields fields={4} />
      ) : (
        <div className="space-y-6">
          <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase text-muted-foreground">Reference</dt>
              <dd className="text-sm font-medium text-foreground">
                {selectedTransaction.reference || selectedTransaction.id}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted-foreground">Channel</dt>
              <dd className="text-sm font-medium text-foreground">
                {selectedTransaction.channelLabel || selectedTransaction.salesChannel || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted-foreground">
                {isPos ? 'Sale ref' : 'Order / ref'}
              </dt>
              <dd className="text-sm font-medium text-foreground">
                {selectedTransaction.orderNumber || '-'}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted-foreground">Amount</dt>
              <dd className="text-sm font-medium text-foreground tabular-nums">
                {selectedTransaction.amount != null && selectedTransaction.amount !== ''
                  ? isPos
                    ? formatMoney(Number(selectedTransaction.amount), currency)
                    : formatMoneyFromUnknown(selectedTransaction.amount, currency)
                  : '-'}
              </dd>
            </div>
            {isPos && selectedTransaction.subtotal != null ? (
              <div>
                <dt className="text-xs uppercase text-muted-foreground">Subtotal</dt>
                <dd className="text-sm font-medium text-foreground tabular-nums">
                  {formatMoney(Number(selectedTransaction.subtotal), currency)}
                </dd>
              </div>
            ) : null}
            {isPos && selectedTransaction.taxTotal != null ? (
              <div>
                <dt className="text-xs uppercase text-muted-foreground">Tax</dt>
                <dd className="text-sm font-medium text-foreground tabular-nums">
                  {formatMoney(Number(selectedTransaction.taxTotal), currency)}
                </dd>
              </div>
            ) : null}
            <div>
              <dt className="text-xs uppercase text-muted-foreground">Status</dt>
              <dd className="text-sm font-medium text-foreground">{selectedTransaction.status || '-'}</dd>
            </div>
            {isPos && selectedTransaction.staff ? (
              <div className="sm:col-span-2">
                <dt className="text-xs uppercase text-muted-foreground">Staff</dt>
                <dd className="text-sm font-medium text-foreground">
                  {[selectedTransaction.staff.firstName, selectedTransaction.staff.lastName]
                    .filter(Boolean)
                    .join(' ') || '—'}
                  {selectedTransaction.staff.email ? (
                    <span className="text-muted-foreground"> · {selectedTransaction.staff.email}</span>
                  ) : null}
                </dd>
              </div>
            ) : null}
          </dl>
          {isPos && Array.isArray(selectedTransaction.lines) && selectedTransaction.lines.length > 0 ? (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Line items</h3>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full min-w-[320px] text-left text-sm">
                  <thead className="border-b border-border bg-muted/30 text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">S/N</th>
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Line total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTransaction.lines.map((line, rowIndex) => (
                      <tr key={line.id} className="border-b border-border/80">
                        <td className="px-3 py-2 tabular-nums">{rowIndex + 1}</td>
                        <td className="px-3 py-2">{line.productName || line.productSlug || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{line.quantity}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatMoney(Number(line.lineTotal), currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  )
}
